import type { AppDispatch, RootState } from '../../store';
import { addElements, removeElements } from './layoutSlice';
import { addHistoryAction } from '../history/historySlice';
import { selectElements } from '../selection/selectionSlice';
import { generateAutoRoute, AutoRouteSummary, RouteMode } from '../../utils/trackAutoRouter';
import { generateId } from '../../utils/geometry';
import { LayoutElement } from '../../types';

const EMPTY_SUMMARY = (message: string): AutoRouteSummary => ({
  railCount: 0,
  curveCount: 0,
  junctionCount: 0,
  totalLengthWorldUnits: 0,
  skippedStockerIds: [],
  message,
});

/**
 * Auto-routes track from the selected (or first) OHT vehicle to the selected
 * (or all) stockers on the canvas, replacing any track this feature
 * previously generated for that same OHT. Dispatches the whole add/remove as
 * a single undoable step. `mode: 'tree'` (default) dead-ends a branch at
 * each stocker; `mode: 'loop'` visits every stocker in one closed ring.
 */
export const generateRoute =
  (mode: RouteMode = 'tree') =>
  (dispatch: AppDispatch, getState: () => RootState): AutoRouteSummary => {
    const state = getState();
    const elements = Object.values(state.layout.elements);
    const selectedIds = state.selection.selectedIds;

    const ohtVehicles = elements.filter((e) => e.type === 'oht-vehicle');
    const oht = ohtVehicles.find((e) => selectedIds.includes(e.id)) ?? ohtVehicles[0];
    if (!oht) {
      return EMPTY_SUMMARY('Add an OHT vehicle to the layout before generating a route.');
    }

    const allStockers = elements.filter((e) => e.type === 'stocker');
    const selectedStockers = allStockers.filter((e) => selectedIds.includes(e.id));
    // A loop needs at least 2 stops, so a selection too small to loop through
    // (0 or 1 stocker selected) falls back to routing every stocker instead —
    // otherwise whichever stocker was placed last (and so stayed selected)
    // would silently limit the whole layout to a 1-stocker "loop".
    const selectionUsable = mode === 'loop' ? selectedStockers.length >= 2 : selectedStockers.length > 0;
    const stockers = selectionUsable ? selectedStockers : allStockers;
    if (stockers.length === 0) {
      return EMPTY_SUMMARY('Add at least one stocker to the layout before generating a route.');
    }

    // Auto-routed track is scoped per OHT via autoRouteGroupId === oht.id, so
    // regenerating replaces this OHT's previous track instead of duplicating it.
    const groupId = oht.id;
    const previousAutoRouted = elements.filter((e) => e.customProperties?.autoRouteGroupId === groupId);

    const { elements: newElements, summary } = generateAutoRoute(oht, stockers, elements, { groupId, mode });

    if (previousAutoRouted.length > 0) {
      dispatch({
        type: removeElements.type,
        payload: previousAutoRouted.map((e) => e.id),
        meta: { skipHistory: true },
      });
    }

    if (newElements.length > 0) {
      dispatch({ type: addElements.type, payload: newElements, meta: { skipHistory: true } });
      dispatch(
        addHistoryAction({
          id: generateId(),
          type: 'replace',
          timestamp: Date.now(),
          elementIds: newElements.map((e) => e.id),
          previousState: previousAutoRouted as Partial<LayoutElement>[],
          newState: newElements as Partial<LayoutElement>[],
        })
      );
      dispatch(selectElements(newElements.map((e) => e.id)));
    }

    return summary;
  };
